import type { SocialUrls } from '@/app_lib/types/accounts/checkout';

/** Shared message constants for /api/signin/* route handlers. */
export const SIGNIN_API = {
  emailVerificationFailed: `This email address can't be verified. Please enter a valid, active email.`,
  invalidJsonBody: 'Invalid JSON body',
  invalidRequestBody: 'Invalid request body',
  unexpectedError: 'An unexpected error occurred',
} as const;

export type CheckEmailApiError = { success: false; message: string };

export type CheckEmailApiSuccess =
  | { success: true; userType: 'not-found'; message: string }
  | { success: true; userType: 'password'; message: string }
  | { success: true; userType: 'passwordless'; message: string };

export type CheckEmailApiResponse = CheckEmailApiError | CheckEmailApiSuccess;

export type SignInApiError = { success: false; message: string };

export type SignInApiSuccessPassword = {
  success: true;
  userType: 'password';
  message: string;
  accessToken: string;
  pianoUid: string;
};

export type SignInApiSuccessPasswordless = {
  success: true;
  userType: 'passwordless';
  message: string;
};

export type SignInApiResponse = SignInApiError | SignInApiSuccessPassword | SignInApiSuccessPasswordless;

export const isSignInApiSuccessPassword = (response: SignInApiResponse): response is SignInApiSuccessPassword => {
  return response.success && response.userType === 'password';
};

/**
 * JSON body from POST /api/signin/register
 *
 * - Success: `returnZeroBounceStatuses` adds optional `status` / `subStatus` (ZeroBounce).
 * - Error with optional `pianoUid` / ZB fields: account may exist but magic-link email failed (500).
 */
export type RegisterApiSuccess = {
  success: true;
  message: string;
  pianoUid: string;
  status?: string;
  subStatus?: string;
};

export type RegisterApiError = {
  success: false;
  message: string;
  pianoUid?: string;
  status?: string;
  subStatus?: string;
};

export type RegisterApiResponse = RegisterApiSuccess | RegisterApiError;

export type SocialSignInApiError = { success: false; message: string };

export type SocialSignInApiSuccess = {
  success: true;
  message: string;
  accessToken: string;
  pianoUid: string;
  registration: boolean;
};

export type SocialSignInApiResponse = SocialSignInApiError | SocialSignInApiSuccess;

export type ResetPasswordApiResponse = { success: boolean; message: string };

// TODO: Move the typing for these functions to this file when we migrate the Checkout flow to use the new signin flow.
export type SocialUrlsApiSuccess = { success: true; socialUrls: SocialUrls };

export type SocialUrlsApiError = {
  success: false;
  message: string;
  socialUrls?: SocialUrls;
};
